import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { z } from 'zod';
import {
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
  Package,
  Plus,
  Search,
  Trash2,
} from 'lucide-react';
import { PartStatus } from '@ncc/shared';
import { api } from '../lib/api';
import { formatDateTime, formatMoney, listingPrice } from '../lib/format';

const StatusFilter = z.union([PartStatus, z.literal('all')]);
type StatusFilter = z.infer<typeof StatusFilter>;

export const PartListSearchSchema = z.object({
  q: z.string().optional(),
  status: StatusFilter.optional(),
  brand_id: z.coerce.number().int().optional(),
  category_id: z.coerce.number().int().optional(),
  page: z.coerce.number().int().min(1).optional(),
  page_size: z.coerce.number().int().min(1).max(100).optional(),
  sort: z.enum(['newest', 'oldest', 'price_asc', 'price_desc', 'name_asc', 'name_desc']).optional(),
});
export type PartListSearch = z.infer<typeof PartListSearchSchema>;

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 25;
const DEFAULT_SORT = 'newest' as const;
const DEFAULT_STATUS: StatusFilter = 'published';

export function PartList() {
  const search = useSearch({ from: '/parts' }) as PartListSearch;
  const navigate = useNavigate({ from: '/parts' });
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const brandsQ = useQuery({ queryKey: ['brands'], queryFn: api.listBrands });
  const categoriesQ = useQuery({ queryKey: ['part-categories'], queryFn: api.listPartCategories });

  const activeStatus: StatusFilter = search.status ?? DEFAULT_STATUS;
  const effective = {
    ...search,
    status: activeStatus === 'all' ? undefined : activeStatus,
    page: search.page ?? DEFAULT_PAGE,
    page_size: search.page_size ?? DEFAULT_PAGE_SIZE,
    sort: search.sort ?? DEFAULT_SORT,
  };

  const listQ = useQuery({
    queryKey: ['parts', effective],
    queryFn: () => api.listParts(effective),
    placeholderData: (p) => p,
  });

  const bulk = useMutation({
    mutationFn: api.bulkParts,
    onSuccess: () => {
      setSelected(new Set());
      queryClient.invalidateQueries({ queryKey: ['parts'] });
    },
  });

  const items = listQ.data?.items ?? [];
  const allChecked = items.length > 0 && items.every((i) => selected.has(i.id));

  const update = (patch: Partial<PartListSearch>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch, page: patch.page ?? 1 }) });

  const totalPages = useMemo(() => {
    if (!listQ.data) return 1;
    return Math.max(1, Math.ceil(listQ.data.total / listQ.data.page_size));
  }, [listQ.data]);

  return (
    <>
      <header className="toolbar">
        <h2 style={{ margin: 0 }}>Parts</h2>
        <span className="grow" />
        <Link to="/parts/new">
          <button className="primary">
            <Plus size={16} /> New Part
          </button>
        </Link>
      </header>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="toolbar">
          <div style={{ position: 'relative', maxWidth: 360, flex: '1 1 240px' }}>
            <Search
              size={14}
              style={{
                position: 'absolute',
                left: 10,
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--muted)',
              }}
            />
            <input
              placeholder="Search name, description, part #, SKU…"
              defaultValue={search.q ?? ''}
              onKeyDown={(e) => {
                if (e.key === 'Enter') update({ q: (e.target as HTMLInputElement).value });
              }}
              style={{ paddingLeft: 30 }}
            />
          </div>
          <select
            value={activeStatus}
            onChange={(e) => update({ status: e.target.value as StatusFilter })}
            style={{ maxWidth: 160 }}
          >
            <option value="all">All statuses</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
            <option value="hidden">Hidden</option>
            <option value="out_of_stock">Out of stock</option>
          </select>
          <select
            value={search.brand_id ?? ''}
            onChange={(e) =>
              update({ brand_id: e.target.value ? Number(e.target.value) : undefined })
            }
            style={{ maxWidth: 200 }}
          >
            <option value="">All brands</option>
            {brandsQ.data?.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <select
            value={search.category_id ?? ''}
            onChange={(e) =>
              update({ category_id: e.target.value ? Number(e.target.value) : undefined })
            }
            style={{ maxWidth: 220 }}
          >
            <option value="">All categories</option>
            {categoriesQ.data?.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <select
            value={effective.sort}
            onChange={(e) => update({ sort: e.target.value as PartListSearch['sort'] })}
            style={{ maxWidth: 180 }}
          >
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="price_desc">Highest price</option>
            <option value="price_asc">Lowest price</option>
            <option value="name_asc">Name A–Z</option>
            <option value="name_desc">Name Z–A</option>
          </select>
        </div>
      </div>

      {selected.size > 0 ? (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div className="toolbar">
            <strong>{selected.size} selected</strong>
            <button
              onClick={() => bulk.mutate({ action: 'publish', ids: [...selected] })}
              disabled={bulk.isPending}
            >
              <Eye size={14} /> Publish
            </button>
            <button
              onClick={() => bulk.mutate({ action: 'hide', ids: [...selected] })}
              disabled={bulk.isPending}
            >
              <EyeOff size={14} /> Hide
            </button>
            <button
              onClick={() => bulk.mutate({ action: 'mark_out_of_stock', ids: [...selected] })}
              disabled={bulk.isPending}
            >
              <Package size={14} /> Mark out of stock
            </button>
            <button
              className="danger"
              onClick={() => {
                if (confirm(`Delete ${selected.size} part(s)? This cannot be undone.`)) {
                  bulk.mutate({ action: 'delete', ids: [...selected] });
                }
              }}
              disabled={bulk.isPending}
            >
              <Trash2 size={14} /> Delete
            </button>
            <span className="grow" />
            <button className="ghost" onClick={() => setSelected(new Set())}>
              Clear
            </button>
          </div>
        </div>
      ) : null}

      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th style={{ width: 36 }}>
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={(e) => {
                    if (e.target.checked) setSelected(new Set(items.map((i) => i.id)));
                    else setSelected(new Set());
                  }}
                />
              </th>
              <th style={{ width: 88 }}>Photo</th>
              <th style={{ width: 110 }}>Status</th>
              <th className="wrap">Name</th>
              <th style={{ width: 140 }}>Brand</th>
              <th style={{ width: 160 }}>Category</th>
              <th style={{ width: 130 }}>Part #</th>
              <th style={{ width: 130, textAlign: 'right' }}>Price</th>
              <th style={{ width: 180 }}>Updated</th>
            </tr>
          </thead>
          <tbody>
            {listQ.isLoading ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: '2rem' }}>
                  Loading…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td
                  colSpan={9}
                  style={{ textAlign: 'center', padding: '2rem', color: 'var(--muted)' }}
                >
                  No parts match the current filters.
                </td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(item.id)}
                      onChange={(e) => {
                        setSelected((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(item.id);
                          else next.delete(item.id);
                          return next;
                        });
                      }}
                    />
                  </td>
                  <td>
                    <div
                      className="thumb"
                      style={
                        item.images[0]?.url
                          ? { backgroundImage: `url(${item.images[0].url})` }
                          : undefined
                      }
                      aria-label={item.images[0]?.url ? '' : 'No image'}
                    />
                  </td>
                  <td>
                    <span className={`badge ${item.status}`}>{item.status.replace('_', ' ')}</span>
                  </td>
                  <td className="wrap">
                    <Link
                      to="/parts/edit/$id"
                      params={{ id: String(item.id) }}
                      style={{ fontWeight: 500, color: 'var(--ink)' }}
                    >
                      {item.name}
                    </Link>
                    {item.sku ? (
                      <div style={{ color: 'var(--muted)', fontSize: '0.8rem' }}>
                        SKU {item.sku}
                      </div>
                    ) : null}
                  </td>
                  <td>{item.brand_name ?? '—'}</td>
                  <td>{item.category_name ?? '—'}</td>
                  <td style={{ fontFamily: 'monospace' }}>{item.part_number}</td>
                  <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {formatMoney(listingPrice(item))}
                  </td>
                  <td style={{ color: 'var(--muted)' }}>{formatDateTime(item.updated_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="toolbar" style={{ marginTop: '1rem', justifyContent: 'center' }}>
        <button
          onClick={() => update({ page: Math.max(1, (search.page ?? 1) - 1) })}
          disabled={(search.page ?? 1) <= 1}
        >
          <ChevronLeft size={14} /> Prev
        </button>
        <span style={{ alignSelf: 'center', color: 'var(--muted)' }}>
          Page {effective.page} of {totalPages} ({listQ.data?.total ?? 0} total)
        </span>
        <button
          onClick={() => update({ page: Math.min(totalPages, (search.page ?? 1) + 1) })}
          disabled={(search.page ?? 1) >= totalPages}
        >
          Next <ChevronRight size={14} />
        </button>
      </div>
    </>
  );
}
